import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import ThemeToggle from './ThemeToggle';
import { apiService } from '../services/api';

const Navbar: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuthStore();
  const location = useLocation();
  const [showServiceTokenModal, setShowServiceTokenModal] = useState(false);
  const [serviceTokenEmail, setServiceTokenEmail] = useState('service-account@neubird.local');
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [tokenExpiry, setTokenExpiry] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    logout();
  };

  const handleGenerateServiceToken = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const response = await apiService.createServiceAccountToken(serviceTokenEmail);
      setGeneratedToken(response.token.access_token);
      setTokenExpiry(response.token.expires_at);
    } catch (err: any) {
      setError(err.message || 'Failed to generate service account token');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyToken = () => {
    if (generatedToken) {
      navigator.clipboard.writeText(generatedToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const closeModal = () => {
    setShowServiceTokenModal(false);
    setGeneratedToken(null);
    setTokenExpiry(null);
    setError(null);
    setCopied(false);
  };

  const handleClearAll = async () => {
    const confirmed = window.confirm(
      '⚠️ WARNING: This will delete ALL messages and reset all user balances to $0.\n\n' +
      'This action cannot be undone. Are you sure you want to continue?'
    );

    if (!confirmed) {
      return;
    }

    setIsClearing(true);
    setError(null);
    try {
      const result = await apiService.clearAllMessages();
      alert(
        `✅ Successfully cleared all data!\n\n` +
        `Messages deleted: ${result.messages_deleted}\n` +
        `Tracking events deleted: ${result.tracking_events_deleted}\n` +
        `Users reset: ${result.users_reset}\n\n` +
        `You will now be logged out.`
      );
      // Log out after clearing
      logout();
    } catch (err: any) {
      setError(err.message || 'Failed to clear all messages');
      alert(`❌ Error: ${err.message || 'Failed to clear all messages'}`);
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <nav className="bg-white dark:bg-gray-800 shadow-lg border-b border-gray-200 dark:border-gray-700 transition-colors">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Brand */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="text-2xl">🦅</div>
            <span className="text-xl font-bold text-gray-800 dark:text-gray-100">
              Neubird Messaging
            </span>
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-6">
            <Link
              to="/"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/') 
                  ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20' 
                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-300 dark:hover:text-blue-400 dark:hover:bg-blue-900/20'
              }`}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive('/about') 
                  ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20' 
                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-300 dark:hover:text-blue-400 dark:hover:bg-blue-900/20'
              }`}
            >
              About
            </Link>
            
            {isAuthenticated ? (
              <>
                <Link
                  to="/dashboard"
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive('/dashboard') 
                      ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20' 
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-300 dark:hover:text-blue-400 dark:hover:bg-blue-900/20'
                  }`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/send"
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive('/send') 
                      ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20' 
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-300 dark:hover:text-blue-400 dark:hover:bg-blue-900/20'
                  }`}
                >
                  Send Message
                </Link>
                <Link
                  to="/messages"
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive('/messages') 
                      ? 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20' 
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-300 dark:hover:text-blue-400 dark:hover:bg-blue-900/20'
                  }`}
                >
                  My Messages
                </Link>
              </>
            ) : null}
          </div>

          {/* User Menu / Auth Buttons */}
          <div className="flex items-center space-x-4">
            {isAuthenticated && user ? (
              <div className="flex items-center space-x-4">
                <ThemeToggle />
                {/* Account Spending */}
                <div className="hidden sm:flex items-center space-x-2 bg-blue-50 dark:bg-blue-900/20 px-3 py-1 rounded-full">
                  <span className="text-blue-600 dark:text-blue-400 text-sm font-medium">
                    Total Spent: ${(user.total_spent || 0).toFixed(2)}
                  </span>
                </div>
                
                {/* User Menu */}
                <div className="relative group">
                  <button className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 focus:outline-none">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="hidden sm:block text-sm font-medium">
                      {user.name}
                    </span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-700 rounded-md shadow-lg py-1 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                    >
                      Profile Settings
                    </Link>
                    <Link
                      to="/messages"
                      className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                    >
                      Message History
                    </Link>
                    <button
                      onClick={() => setShowServiceTokenModal(true)}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                    >
                      🔑 Generate Service Token
                    </button>
                    <button
                      onClick={handleClearAll}
                      disabled={isClearing}
                      className="block w-full text-left px-4 py-2 text-sm text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isClearing ? '🔄 Clearing...' : '🗑️ Clear All Messages'}
                    </button>
                    <hr className="my-1 border-gray-200 dark:border-gray-600" />
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <ThemeToggle />
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button className="text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100 focus:outline-none">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Service Account Token Generation Modal */}
      {showServiceTokenModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  🔑 Generate Service Account Token
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Create a long-lived token (10 years) for automated testing and API integrations
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {!generatedToken ? (
                <>
                  {/* Email Input Form */}
                  <div>
                    <label htmlFor="serviceEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Service Account Email
                    </label>
                    <input
                      type="email"
                      id="serviceEmail"
                      value={serviceTokenEmail}
                      onChange={(e) => setServiceTokenEmail(e.target.value)}
                      placeholder="service-account@neubird.local"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      💡 Tip: Use descriptive names like <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">ci-cd@neubird.local</code> or <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">monitoring@neubird.local</code>
                    </p>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
                      <p className="text-sm text-red-600 dark:text-red-400">
                        ❌ {error}
                      </p>
                    </div>
                  )}

                  {/* Generate Button */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      onClick={closeModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleGenerateServiceToken}
                      disabled={isGenerating || !serviceTokenEmail}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isGenerating ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Generating...
                        </span>
                      ) : (
                        '🔑 Generate Token'
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Success Message */}
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
                    <p className="text-sm font-medium text-green-800 dark:text-green-400">
                      ✅ Service account token generated successfully!
                    </p>
                  </div>

                  {/* Token Details */}
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email
                      </label>
                      <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                        <code className="text-sm text-gray-800 dark:text-gray-200">{serviceTokenEmail}</code>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Expires
                      </label>
                      <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                        <code className="text-sm text-gray-800 dark:text-gray-200">
                          {tokenExpiry ? new Date(tokenExpiry).toLocaleString() : 'N/A'} (10 years)
                        </code>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Access Token
                      </label>
                      <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                        <code className="text-xs text-gray-800 dark:text-gray-200 break-all font-mono">
                          {generatedToken}
                        </code>
                      </div>
                      <button
                        onClick={handleCopyToken}
                        className="mt-2 w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-md transition-colors"
                      >
                        {copied ? '✓ Copied!' : '📋 Copy Token'}
                      </button>
                    </div>
                  </div>

                  {/* Usage Instructions */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-400 mb-2">
                      💡 Usage Instructions
                    </p>
                    <div className="space-y-2 text-xs text-blue-700 dark:text-blue-300">
                      <p><strong>In HTTP requests:</strong></p>
                      <code className="block bg-blue-100 dark:bg-blue-900/40 p-2 rounded font-mono">
                        Authorization: Bearer {generatedToken?.substring(0, 40)}...
                      </code>
                      <p className="mt-2"><strong>In environment variables:</strong></p>
                      <code className="block bg-blue-100 dark:bg-blue-900/40 p-2 rounded font-mono">
                        export SERVICE_ACCOUNT_TOKEN="{generatedToken?.substring(0, 40)}..."
                      </code>
                    </div>
                  </div>

                  {/* Close Button */}
                  <div className="flex justify-end pt-4">
                    <button
                      onClick={closeModal}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-md transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
